import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Mail, Pencil, Trash2, AlertTriangle, CheckCircle2, X, UserPlus, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useAuth } from "@/lib/auth";
import { fmtDateTime } from "@/lib/constants";

const EMPTY = { name: "", subject: "", body_html: "", category: "quote_send" };
const CATEGORIES = ["quote_send", "po_send", "quote", "followup", "install", "handover", "internal", "other"];
const CAT_LABELS = { quote_send: "Quote send (customer)", po_send: "PO send (supplier)" };
const SEND_VARS = {
  quote_send: "{{customer_name}} · {{first_name}} · {{quote_number}} · {{sale_price}} · {{total_inc_gst}} · {{fence_type}} · {{site_address}}",
  po_send: "{{po_number}} · {{contact_name}} · {{supplier_name}} · {{expected_delivery}}",
};

export default function EmailTemplates() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const { data: templates = [] } = useQuery({ queryKey: ["templates"], queryFn: async () => (await api.get("/email/templates")).data });
  const { data: status } = useQuery({ queryKey: ["email-status"], queryFn: async () => (await api.get("/email/status")).data });
  const { data: logs = [] } = useQuery({ queryKey: ["email-logs"], queryFn: async () => (await api.get("/email-activity", { params: { limit: 25 } })).data.items || [] });

  const save = useMutation({
    mutationFn: async (body) => {
      if (body.id) return (await api.patch(`/email/templates/${body.id}`, body)).data;
      return (await api.post("/email/templates", body)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["templates"] }); setOpen(false); toast.success("Saved"); },
  });
  const del = useMutation({
    mutationFn: async (id) => (await api.delete(`/email/templates/${id}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["templates"] }),
  });

  return (
    <div>
      <PageHeader
        title="Email Templates & Logs"
        subtitle="Pre-built email templates with variables like {{customer_name}}, {{quote_number}}, {{sale_price}}."
        breadcrumb={["System", "Email"]}
        actions={
          <Button data-testid="new-template-btn" onClick={() => { setEditing({ ...EMPTY }); setOpen(true); }} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={16} className="mr-2" /> New template
          </Button>
        }
      />

      <div className="px-6 lg:px-10 py-6 space-y-5">
        {/* Status banner */}
        {status && (
          <Card className={`rounded-sm border p-4 flex items-start gap-3 ${status.live ? "border-emerald-200 bg-emerald-50" : "border-amber-200 bg-amber-50"}`} data-testid="email-status-banner">
            {status.live ? <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" /> : <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />}
            <div className="text-sm">
              <div className="font-display font-semibold text-slate-900">
                {status.live ? "Live" : "Mock mode"} · sending from <span className="font-mono">{status.sender}</span>
              </div>
              <p className="text-slate-700 mt-0.5">{status.message}</p>
            </div>
          </Card>
        )}

        {/* Auto-CC internal staff (Trent, Nov 2026) */}
        <AutoCcCard />

        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 stamp-label text-slate-500">Templates ({templates.length})</div>
          <div className="divide-y divide-slate-100">
            {templates.map((t) => (
              <div key={t.id} className="px-5 py-4 hover:bg-cyan-50/30 flex items-start gap-4" data-testid={`template-${t.id}`}>
                <div className="w-9 h-9 bg-cyan-100 flex items-center justify-center text-cyan-700 shrink-0"><Mail size={15} /></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-semibold text-slate-900">{t.name}</div>
                    {t.category && <Badge variant="outline" className={`rounded-sm text-[10px] ${CAT_LABELS[t.category] ? "border-cyan-300 bg-cyan-50 text-cyan-800" : "border-slate-300"}`}>{CAT_LABELS[t.category] || t.category}</Badge>}
                    {t.key && <span className="font-mono text-[10px] text-slate-400">{t.key}</span>}
                  </div>
                  <div className="text-sm text-slate-700 mt-1">📨 {t.subject}</div>
                  <div className="text-xs text-slate-500 mt-1 font-mono truncate">{(t.body_html || "").replace(/<[^>]*>/g, " ").slice(0, 140) + "…"}</div>
                </div>
                <div className="flex gap-1 shrink-0">
                  <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => { setEditing({ ...t }); setOpen(true); }} data-testid={`edit-template-${t.id}`}><Pencil size={13} /></Button>
                  {!["quote_send_default", "po_send_default"].includes(t.key) && (
                    <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-rose-600" onClick={() => del.mutate(t.id)}><Trash2 size={13} /></Button>
                  )}
                </div>
              </div>
            ))}
            {templates.length === 0 && <div className="py-12 text-center text-slate-400 text-sm">No templates yet</div>}
          </div>
        </Card>

        <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 stamp-label text-slate-500">Recent sends ({logs.length})</div>
          <div className="divide-y divide-slate-100">
            {logs.map((l) => (
              <div key={`${l.source}-${l.id}`} className="px-5 py-3 flex items-center gap-3" data-testid={`log-${l.id}`}>
                <Badge variant="outline" className={`rounded-sm text-[10px] ${l.status === "sent" ? "border-emerald-300 bg-emerald-50 text-emerald-800" : l.status === "mocked" ? "border-amber-300 bg-amber-50 text-amber-800" : "border-rose-300 bg-rose-50 text-rose-800"}`}>
                  {l.status}
                </Badge>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">{l.subject}</div>
                  <div className="text-[11px] text-slate-500">→ {l.to} · {fmtDateTime(l.when)} · {l.kind}</div>
                  {(l.cc || []).length > 0 && (
                    <div className="text-[10px] text-cyan-700" data-testid={`log-cc-${l.id}`}>CC: {l.cc.join(", ")}</div>
                  )}
                </div>
              </div>
            ))}
            {logs.length === 0 && <div className="py-8 text-center text-slate-400 text-sm">No emails sent yet</div>}
          </div>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm sm:max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-xl">{editing?.id ? "Edit template" : "New template"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Template name" required><Input data-testid="tpl-name-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></Field>
                <Field label="Category">
                  <Select value={editing.category || "quote_send"} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="tpl-category-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{CAT_LABELS[c] || c}</SelectItem>)}</SelectContent>
                  </Select>
                </Field>
              </div>
              {SEND_VARS[editing.category] && (
                <div className="text-xs text-cyan-900 bg-cyan-50 border border-cyan-200 p-3 rounded-sm" data-testid="send-template-note">
                  This template appears in the <b>Template dropdown</b> of the {editing.category === "quote_send" ? "quote send popup" : "Email PO popup"}.
                  Write the body as <b>plain text</b> — it slots into the branded CCFI email (logo, {editing.category === "quote_send" ? "review & accept button" : "PO header"} and footer are added automatically{editing.category === "quote_send" ? "; pricing is only in the attached PDF" : ""}).
                  The template keyed <span className="font-mono">{editing.category}_default</span> provides the standard wording.
                </div>
              )}
              <Field label="Subject line" required><Input data-testid="tpl-subject-input" value={editing.subject} onChange={(e) => setEditing({ ...editing, subject: e.target.value })} /></Field>
              <Field label={SEND_VARS[editing.category] ? "Message (plain text)" : "HTML body"}>
                <Textarea data-testid="tpl-body-input" rows={12} className="font-mono text-xs" value={editing.body_html} onChange={(e) => setEditing({ ...editing, body_html: e.target.value })} />
              </Field>
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 p-3 rounded-sm">
                <div className="stamp-label text-slate-600 mb-1">Available variables</div>
                <div className="font-mono leading-relaxed">
                  {SEND_VARS[editing.category] ||
                    `{{customer_name}} · {{quote_number}} · {{fence_type}} · {{site_address}} · {{scope_summary}} · {{sale_price}} · {{linear_meters}} · {{deal_title}} · {{deal_value}} · {{company_name}} · {{job_number}} · {{scheduled_start}}`}
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="save-template-btn" onClick={() => save.mutate(editing)} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CcStaffRow({ label, sub, users, staff, onChange, saving, testPrefix, readOnly }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const current = new Set(users.map((u) => u.id));
  const options = staff.filter((s) =>
    !current.has(s.id) &&
    ((s.name || "") + (s.email || "")).toLowerCase().includes(search.trim().toLowerCase()));
  return (
    <div className="px-5 py-4 flex items-start gap-6 flex-wrap" data-testid={`${testPrefix}-row`}>
      <div className="w-64 shrink-0">
        <div className="text-sm font-semibold text-slate-900">{label}</div>
        <div className="text-[11px] text-slate-400">{sub}</div>
      </div>
      <div className="flex-1 flex items-center gap-2 flex-wrap min-w-[240px]">
        {users.map((u) => (
          <span key={u.id} className="inline-flex items-center gap-1.5 bg-cyan-50 border border-cyan-200 text-cyan-800 rounded-full px-3 py-1 text-xs font-medium" data-testid={`${testPrefix}-chip-${u.id}`}>
            {u.name || u.email}
            {!readOnly && (
              <button type="button" className="text-cyan-500 hover:text-rose-600" disabled={saving}
                onClick={() => onChange(users.filter((x) => x.id !== u.id).map((x) => x.id))}
                data-testid={`${testPrefix}-remove-${u.id}`}>
                <X size={11} />
              </button>
            )}
          </span>
        ))}
        {readOnly && users.length === 0 && <span className="text-xs text-slate-400">No one — ask an admin to set this up</span>}
        {!readOnly && (
        <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) setSearch(""); }}>
          <PopoverTrigger asChild>
            <button type="button" disabled={saving}
              className="inline-flex items-center gap-1.5 border border-dashed border-slate-300 text-slate-500 rounded-full px-3 py-1 text-xs hover:border-cyan-400 hover:text-cyan-600"
              data-testid={`${testPrefix}-add-btn`}>
              {saving ? <Loader2 size={11} className="animate-spin" /> : <UserPlus size={11} />} Add staff
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-0 rounded-sm" align="start">
            <div className="p-2 border-b border-slate-100">
              <Input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
                placeholder="Search staff…" className="h-8 rounded-sm text-xs" data-testid={`${testPrefix}-search`} />
            </div>
            <div className="max-h-52 overflow-y-auto">
              {options.map((s) => (
                <button key={s.id} type="button"
                  className="w-full text-left px-3 py-2 text-sm hover:bg-cyan-50 flex items-center justify-between gap-2"
                  onClick={() => { onChange([...users.map((x) => x.id), s.id]); setOpen(false); setSearch(""); }}
                  data-testid={`${testPrefix}-opt-${s.id}`}>
                  <span className="truncate">{s.name || s.email}</span>
                  <span className="text-[10px] text-slate-400 truncate max-w-[110px]">{s.email}</span>
                </button>
              ))}
              {options.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">No matching staff</div>}
            </div>
          </PopoverContent>
        </Popover>
        )}
      </div>
    </div>
  );
}

function AutoCcCard() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = ["admin", "super_admin"].includes(user?.role);
  const { data } = useQuery({ queryKey: ["auto-cc"], queryFn: async () => (await api.get("/email/auto-cc")).data });
  const { data: staff = [] } = useQuery({ queryKey: ["mentionable-users"], queryFn: async () => (await api.get("/auth/users/mentionable")).data });
  const save = useMutation({
    mutationFn: async (body) => (await api.put("/email/auto-cc", body)).data,
    onSuccess: (d) => { qc.setQueryData(["auto-cc"], d); toast.success("Auto-CC updated"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not save auto-CC"),
  });
  const quoteCc = data?.quote_cc || [];
  const poCc = data?.po_cc || [];
  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow overflow-hidden" data-testid="auto-cc-card">
      <div className="px-5 py-3 border-b border-slate-200 bg-slate-50">
        <div className="stamp-label text-slate-500">Auto-CC Internal Staff</div>
        <div className="text-xs text-slate-500 mt-0.5">
          Selected staff are automatically CC'd every time these emails leave the system — first sends and revisions. Staff can remove the CC for a single send in the email popup.
          {!isAdmin && <span className="text-amber-700"> Only admins can change these lists.</span>}
        </div>
      </div>
      <div className="divide-y divide-slate-100">
        <CcStaffRow label="Quotes to clients" sub="Send quote to customer · Revised quote"
          users={quoteCc} staff={staff} saving={save.isPending} testPrefix="autocc-quote" readOnly={!isAdmin}
          onChange={(ids) => save.mutate({ quote_cc: ids, po_cc: poCc.map((u) => u.id) })} />
        <CcStaffRow label="Purchase orders to suppliers" sub="Standard PO email · PO revisions"
          users={poCc} staff={staff} saving={save.isPending} testPrefix="autocc-po" readOnly={!isAdmin}
          onChange={(ids) => save.mutate({ quote_cc: quoteCc.map((u) => u.id), po_cc: ids })} />
      </div>
      <div className="px-5 py-2.5 border-t border-slate-100 bg-slate-50/50 text-[11px] text-slate-500">
        Changes save instantly · CC'd addresses show on each outgoing email and are recorded in the email log below.
        <span className="text-amber-700"> Note: while your Resend sending domain is unverified, CC'ing addresses outside your own inbox can cause the whole send to be rejected.</span>
      </div>
    </Card>
  );
}
